import type { SemNode } from 'types/data/semNode';

export interface ApiNodeProperty {
  isAutoId: boolean;
  isPrimary: boolean;
  type: string;
  value: unknown;
}

export interface ApiDecompositionNode {
  id: string;
  hasChildren: boolean;
  properties: Record<string, ApiNodeProperty>;
}

export interface ApiNewNode {
  properties: Record<string, string | number | boolean | null>;
}

/** Single node detail returned by the permission-safe node detail endpoint. */
export interface ApiNodeDetail {
  id: string;
  properties: Record<string, ApiNodeProperty>;
}

/** Narrow a property's `unknown` value to the primitive union `SemNode.Properties` expects. */
export function toPropertyValue(value: unknown): string | number | boolean | null {
  return value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? value
    : null;
}

// Skip properties with these keys for now
const SYSTEM_PROPERTY_KEYS = new Set(['id', 'librarynodeid', 'patternid']);

/** Mirrors backend `IsSystemProperty` — case-insensitive key match for SignalR payloads. */
export function isSystemProperty(key: string): boolean {
  return key.startsWith('_') || SYSTEM_PROPERTY_KEYS.has(key.toLowerCase());
}

/** Placeholder metadata when a property is not yet in the permission-filtered cache. */
export function createPlaceholderApiNodeProperty(value: string | number | boolean | null = null): ApiNodeProperty {
  return { type: '', isPrimary: false, isAutoId: false, value };
}

/**
 * Flatten an `ApiNodeDetail`'s properties from `{ ..., value }` objects into a map of the primitive
 * values the UI renders. The node detail endpoint returns each property as an `ApiNodeProperty`
 * object; callers that display/edit values want just the `value`.
 */
export function flattenNodeDetailProperties(detail: ApiNodeDetail): Record<string, string | number | boolean | null> {
  return Object.fromEntries(
    Object.entries(detail.properties).map(([technicalName, property]) => [
      technicalName,
      toPropertyValue(property.value)
    ])
  );
}

/** Flatten an `ApiNodeDetail` into the `{ Id, Properties }` shape `GRID_STORE.addNodeProperties` expects. */
export function toSemNode(detail: ApiNodeDetail): SemNode {
  return { Id: detail.id, Properties: flattenNodeDetailProperties(detail), Edges: {} };
}

export interface ApiAddedRelation {
  id: string;
  properties: Record<string, string | number | boolean | null>;
  endDataNodeId: string;
  startDataNodeId: string;
}

/** Internal frontend type after transforming API response */
export interface DecompositionNode {
  id: string;
  parentId: string | null;
  title: string;
  subtitle: string;
  hasChildren: boolean;
  /**
   * Client-only marker, never set from an API response: the node was created during this session,
   * so we have observed every child event it has ever had. Set on optimistically pinned nodes by
   * `buildStickyNode` and read by `isNodeCreatedThisSession` (treeCacheLookups.ts).
   */
  createdThisSession?: boolean;
}
